import { useCallback, useEffect, useState } from 'react';
import { api, ApiRequestError } from '../lib/api.js';
import { relativeAge, absoluteTime } from '../lib/format.js';

/**
 * System (new, console redesign v2).
 *
 * `GET /api/risk/status` and the per-factory detail from
 * `GET /api/scanner/status` had zero UI consumer before this page — both
 * routes already existed, already worked, and were simply never wired to
 * anything (the backend design audit that motivated this redesign found
 * exactly that gap). Nothing here is new backend work; this page only
 * surfaces what already existed.
 *
 * Read-only, no kill switch or other control anywhere on it -- that is what
 * the Bot page is for. This page exists to answer "is the risk pipeline
 * healthy", not "can this thing spend money".
 */

interface ProviderBudget {
  provider: string;
  window_start: string;
  requests: number;
  errors: number;
  rate_limited: number;
  max_per_window: number;
}

interface RiskStatusDto {
  liquidity_monitor: {
    enabled: boolean;
    last_checked_at: string | null;
    pairs_checked: number;
    pairs_never_checked: number;
  };
  provenance: {
    enabled: boolean;
    last_check_at: string | null;
    triggers_pending: number;
    triggers_processing: number;
    triggers_failed: number;
    budget: ProviderBudget;
  };
  enrichment: {
    enabled: boolean;
    last_success_at: string | null;
    pairs_enriched: number;
    pairs_stale: number;
    pairs_failing: number;
    budget: ProviderBudget;
  };
  lifecycle_counts: {
    new: number;
    settling: number;
    candidate: number;
    watch: number;
    approval_eligible: number;
    suspected_rug: number;
    rugged: number;
    dead_liquidity: number;
    blocked: number;
    stale: number;
    archived: number;
    unclassified: number;
  };
}

interface FactoryStatus {
  factory_address: string;
  start_block: string;
  last_scanned_block: string;
  last_scanned_at: string | null;
  pairs_found: number;
  consecutive_errors: number;
  last_error: string | null;
  last_error_at: string | null;
  stale: boolean;
}

interface ScannerStatusDto {
  scanner_enabled: boolean;
  chain_id: number;
  rpc_configured: boolean;
  configured_factories: string[];
  awaiting_first_scan: string[];
  factories: FactoryStatus[];
  totals: { pairs: number; tokens: number };
}

/** Same 8 segments the lifecycle-status gate cascade actually produces as
 *  distinct outcomes -- `new`/`settling`/`archived`/`unclassified` are real
 *  values but not shown as their own bar segment (see the note rendered
 *  below the bar, which states the unclassified count rather than hiding
 *  it silently). */
type LifecycleSegmentKey =
  | 'candidate'
  | 'watch'
  | 'approval_eligible'
  | 'rugged'
  | 'suspected_rug'
  | 'dead_liquidity'
  | 'blocked'
  | 'stale';

const LIFECYCLE_SEGMENTS: ReadonlyArray<readonly [LifecycleSegmentKey, string]> = [
  ['candidate', 'Candidates'],
  ['watch', 'Watch'],
  ['approval_eligible', 'Approval eligible'],
  ['rugged', 'Rugged'],
  ['suspected_rug', 'Suspected rug'],
  ['dead_liquidity', 'Dead liquidity'],
  ['blocked', 'Blocked'],
  ['stale', 'Stale'],
];

/** Matches `.lifecycle-segment[data-status]`'s own colours in app.css --
 *  the legend swatches use inline styles rather than relying on the CSS
 *  attribute selector, since a swatch is a plain dot, not the bar itself. */
const LIFECYCLE_COLOR: Record<LifecycleSegmentKey, string> = {
  candidate: 'var(--chrome-accent)',
  watch: 'var(--verdict-watch)',
  approval_eligible: 'oklch(0.9 0.23 123 / 0.55)',
  rugged: 'var(--verdict-avoid)',
  suspected_rug: 'oklch(0.62 0.19 25 / 0.5)',
  dead_liquidity: 'var(--unlit)',
  blocked: 'oklch(0.4 0.01 250)',
  stale: 'oklch(0.3 0.01 250)',
};

function budgetTone(budget: ProviderBudget): 'good' | 'caution' | 'bad' {
  if (budget.max_per_window <= 0) return 'good';
  const used = budget.requests / budget.max_per_window;
  if (used >= 0.9) return 'bad';
  if (used >= 0.7) return 'caution';
  return 'good';
}

function BudgetCard({ budget }: { budget: ProviderBudget }) {
  const tone = budgetTone(budget);
  const pct = budget.max_per_window > 0 ? Math.min(100, (budget.requests / budget.max_per_window) * 100) : 0;
  return (
    <div className="budget-card">
      <div className="budget-head">
        <span className="budget-name">{budget.provider}</span>
        <span className="budget-reading data" data-tone={tone}>
          {budget.requests} / {budget.max_per_window}
          {budget.rate_limited > 0 ? ` · ${budget.rate_limited} rate-limited` : ''}
        </span>
      </div>
      <div className="budget-bar-track">
        <div className="budget-bar-fill" data-tone={tone} style={{ width: `${pct}%` }} />
      </div>
      <p className="panel-note" style={{ marginTop: 'var(--s-1)' }}>
        window started {relativeAge(budget.window_start)} ago
        {budget.errors > 0 ? ` · ${budget.errors} errors this window` : ''}
      </p>
    </div>
  );
}

export function System() {
  const [risk, setRisk] = useState<RiskStatusDto | null>(null);
  const [scanner, setScanner] = useState<ScannerStatusDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [riskResult, scannerResult] = await Promise.all([
        api.get<RiskStatusDto>('/risk/status'),
        api.get<ScannerStatusDto>('/scanner/status'),
      ]);
      setRisk(riskResult);
      setScanner(scannerResult);
      setError(null);
    } catch (err) {
      if (!(err instanceof ApiRequestError && err.isUnauthorized)) {
        setError('Could not load system status.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading && risk === null) {
    return (
      <div className="stack">
        <div className="screen-head">
          <h1 className="screen-title">System</h1>
        </div>
        <p className="notice">Loading system status…</p>
      </div>
    );
  }

  const lifecycleTotal = risk
    ? Object.values(risk.lifecycle_counts).reduce((sum, n) => sum + n, 0)
    : 0;

  return (
    <div className="stack">
      <div className="screen-head">
        <h1 className="screen-title">System</h1>
        <div className="screen-actions">
          <button className="btn btn-mini" type="button" onClick={() => void load()}>
            Refresh
          </button>
        </div>
      </div>

      {error !== null && (
        <p className="notice" data-tone="alarm" role="alert">
          {error}
        </p>
      )}

      {risk && (
        <>
          <div className="grid-2">
            <section className="panel">
              <div className="panel-head">
                <h2 className="panel-title">Liquidity monitor</h2>
                <span className="chip" data-tone={risk.liquidity_monitor.enabled ? 'neutral' : 'muted'}>
                  {risk.liquidity_monitor.enabled ? 'enabled' : 'disabled'}
                </span>
              </div>
              <div className="panel-body">
                <dl className="facts">
                  <div className="fact">
                    <dt className="fact-key">Last checked</dt>
                    <dd className="fact-value" title={absoluteTime(risk.liquidity_monitor.last_checked_at)}>
                      {risk.liquidity_monitor.last_checked_at
                        ? `${relativeAge(risk.liquidity_monitor.last_checked_at)} ago`
                        : 'never'}
                    </dd>
                  </div>
                  <div className="fact">
                    <dt className="fact-key">Pairs checked at least once</dt>
                    <dd className="fact-value">{risk.liquidity_monitor.pairs_checked}</dd>
                  </div>
                  <div className="fact">
                    <dt className="fact-key">Pairs never checked</dt>
                    <dd className="fact-value">{risk.liquidity_monitor.pairs_never_checked}</dd>
                  </div>
                </dl>
              </div>
            </section>

            <section className="panel">
              <div className="panel-head">
                <h2 className="panel-title">Provenance posture</h2>
                <span className="chip" data-tone={risk.provenance.enabled ? 'neutral' : 'muted'}>
                  {risk.provenance.enabled ? 'enabled' : 'disabled'}
                </span>
              </div>
              <div className="panel-body">
                <dl className="facts">
                  <div className="fact">
                    <dt className="fact-key">Last check</dt>
                    <dd className="fact-value" title={absoluteTime(risk.provenance.last_check_at)}>
                      {risk.provenance.last_check_at
                        ? `${relativeAge(risk.provenance.last_check_at)} ago`
                        : 'never'}
                    </dd>
                  </div>
                  <div className="fact">
                    <dt className="fact-key">Triggers pending</dt>
                    <dd className="fact-value">{risk.provenance.triggers_pending}</dd>
                  </div>
                  <div className="fact">
                    <dt className="fact-key">Triggers processing</dt>
                    {/* Staying non-zero across repeated loads is the signal
                        worth noticing -- it means a pass claimed triggers
                        and never completed or failed them. */}
                    <dd className="fact-value">{risk.provenance.triggers_processing}</dd>
                  </div>
                  <div className="fact">
                    <dt className="fact-key">Triggers failed</dt>
                    <dd className="fact-value">{risk.provenance.triggers_failed}</dd>
                  </div>
                </dl>
              </div>
            </section>
          </div>

          <section className="panel">
            <div className="panel-head">
              <h2 className="panel-title">Provider budgets</h2>
              <span className="label label-dim">the numbers behind ADR-059</span>
            </div>
            <div className="panel-body">
              <div className="grid-2">
                <BudgetCard budget={risk.enrichment.budget} />
                <BudgetCard budget={risk.provenance.budget} />
              </div>
            </div>
          </section>

          <section className="panel">
            <div className="panel-head">
              <h2 className="panel-title">Lifecycle status distribution</h2>
              <span className="label label-dim">{lifecycleTotal} pairs total</span>
            </div>
            <div className="panel-body">
              <div className="lifecycle-bar">
                {LIFECYCLE_SEGMENTS.map(([key]) => {
                  const count = risk.lifecycle_counts[key];
                  const pct = lifecycleTotal > 0 ? (count / lifecycleTotal) * 100 : 0;
                  return pct > 0 ? (
                    <div
                      key={key}
                      className="lifecycle-segment"
                      data-status={key}
                      style={{ width: `${pct}%` }}
                      title={`${key}: ${count}`}
                    />
                  ) : null;
                })}
              </div>
              <div className="lifecycle-legend">
                {LIFECYCLE_SEGMENTS.map(([key, label]) => (
                  <span className="lifecycle-legend-item" key={key}>
                    <span className="lifecycle-swatch" style={{ background: LIFECYCLE_COLOR[key] }} />
                    {label}: {risk.lifecycle_counts[key]}
                  </span>
                ))}
              </div>
              {/* No silent gaps: new/settling/archived/unclassified pairs are
                  real counts (usually the largest bucket, mostly pairs that
                  predate the lifecycle cascade or are excluded from
                  rescoring entirely) -- stated here rather than making the
                  bar look like it accounts for every pair. */}
              <p className="panel-note" style={{ marginTop: 'var(--s-3)' }}>
                Not shown above: {risk.lifecycle_counts.unclassified} unclassified,{' '}
                {risk.lifecycle_counts.new} new, {risk.lifecycle_counts.settling} settling,{' '}
                {risk.lifecycle_counts.archived} archived.
              </p>
            </div>
          </section>
        </>
      )}

      {scanner && (
        <section className="panel">
          <div className="panel-head">
            <h2 className="panel-title">Scanner — per factory</h2>
            <span className="chip" data-tone={scanner.scanner_enabled ? 'neutral' : 'muted'}>
              {scanner.scanner_enabled ? 'enabled' : 'disabled'}
            </span>
          </div>
          <div className="table-scroll">
            <table className="ptable">
              <thead>
                <tr>
                  <th scope="col">Factory</th>
                  <th scope="col">Checkpoint block</th>
                  <th scope="col">Staleness</th>
                  <th scope="col">Error</th>
                </tr>
              </thead>
              <tbody>
                {scanner.factories.map((factory) => (
                  <tr key={factory.factory_address}>
                    <td className="data-sm">{factory.factory_address}</td>
                    <td className="ptable-num">{factory.last_scanned_block}</td>
                    <td>
                      <span className="chip" data-tone={factory.stale ? 'alarm' : 'neutral'}>
                        {factory.last_scanned_at
                          ? `${relativeAge(factory.last_scanned_at)} ago`
                          : 'never scanned'}
                      </span>
                      {factory.consecutive_errors > 0 && (
                        <span className="chip" data-tone="alarm" style={{ marginLeft: 'var(--s-2)' }}>
                          {factory.consecutive_errors} consecutive errors
                        </span>
                      )}
                    </td>
                    <td>
                      {factory.last_error ? (
                        <span className="error-text">{factory.last_error}</span>
                      ) : (
                        <span className="token-unknown">none</span>
                      )}
                    </td>
                  </tr>
                ))}
                {scanner.awaiting_first_scan.map((address) => (
                  <tr key={address}>
                    <td className="data-sm">{address}</td>
                    <td className="ptable-num">—</td>
                    <td>
                      <span className="chip" data-tone="alarm">
                        awaiting first scan
                      </span>
                    </td>
                    <td>
                      <span className="token-unknown">n/a</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
